<script setup lang="ts">
/**
 * Компонент для отображения медиа продукта (изображения/видео)
 * Следует Vue Style Guide: Priority A - компонент < 250 строк
 */

import { ref, onMounted, onBeforeUnmount, nextTick, toRefs } from 'vue';
import type { ProductVariant } from '../model/types/product.types';
import { useProductMedia } from '../model/useProductMedia';

interface Props {
  currentVariant: ProductVariant;
  placeholderImg?: string;
  /**
   * У товара есть своя подложка (background_img) — значит сверху лежит PNG
   * с прозрачностью. Кадрировать его нельзя: cover срезал бы края блюда.
   */
  onBackground?: boolean;
}

const props = defineProps<Props>();
const { currentVariant } = toRefs(props);

// Используем композабл для медиа
const {
  mediaItems,
  hasMultipleMedia,
  isVideo,
  setVideoRef,
  handleSlideChange,
  handleVideoLoadedMetadata,
  playFirstVideo,
  stopAllVideos,
} = useProductMedia({ currentVariant });

// Ссылка на swiper контейнер
const swiperContainerRef = ref<HTMLElement | null>(null);

onMounted(() => {
  nextTick(() => {
    playFirstVideo();
  });

  // Подписываемся на события swiper
  nextTick(() => {
    const swiperEl = swiperContainerRef.value;
    if (swiperEl) {
      swiperEl.addEventListener('slidechange', handleSlideChange);
      swiperEl.addEventListener('swiperslidechange', handleSlideChange);
      swiperEl.addEventListener('slideChange', handleSlideChange);
    }
  });
});

onBeforeUnmount(() => {
  // Очищаем слушатели
  const swiperEl = swiperContainerRef.value;
  if (swiperEl) {
    swiperEl.removeEventListener('slidechange', handleSlideChange);
    swiperEl.removeEventListener('swiperslidechange', handleSlideChange);
    swiperEl.removeEventListener('slideChange', handleSlideChange);
  }

  stopAllVideos();
});
</script>

<template>
  <div class="product-media-slider">
    <ClientOnly>
      <!-- Одно изображение - без слайдера -->
      <div v-if="mediaItems.length === 1" class="product-media-slider__single">
        <video
          v-if="isVideo(mediaItems[0].url)"
          :ref="(el) => setVideoRef(el, 0)"
          :src="mediaItems[0].url"
          playsinline
          muted
          loop
          autoplay
          preload="auto"
          class="product-media-slider__media"
          :class="{ 'product-media-slider__media--on-background': onBackground }"
          :alt="currentVariant.name"
        />
        <img
          v-else
          :src="mediaItems[0].url"
          loading="lazy"
          class="product-media-slider__media"
          :class="{ 'product-media-slider__media--on-background': onBackground }"
          :alt="currentVariant.name"
        />
      </div>

      <!-- Несколько изображений - со слайдером -->
      <div v-else-if="hasMultipleMedia" class="product-media-slider__slider">
        <swiper-container
          ref="swiperContainerRef"
          :space-between="10"
          :slides-per-view="1"
          :loop="false"
          :pagination="{ clickable: true }"
          class="product-media-slider__swiper"
          @swiperslidechange="handleSlideChange"
        >
          <swiper-slide
            v-for="(media, index) in mediaItems"
            :key="media.key || index"
            class="product-media-slider__slide"
          >
            <div class="product-media-slider__single">
              <!-- Видео -->
              <video
                v-if="isVideo(media.url)"
                :ref="(el) => setVideoRef(el, index)"
                :src="media.url"
                playsinline
                muted
                loop
                preload="auto"
                class="product-media-slider__media"
          :class="{ 'product-media-slider__media--on-background': onBackground }"
                :alt="currentVariant.name"
                @loadedmetadata="handleVideoLoadedMetadata($event, index)"
              />
              <!-- Изображение -->
              <img
                v-else
                :src="media.url"
                loading="lazy"
                class="product-media-slider__media"
          :class="{ 'product-media-slider__media--on-background': onBackground }"
                :alt="currentVariant.name"
              />
            </div>
          </swiper-slide>
        </swiper-container>
      </div>

      <!-- Плейсхолдер если нет медиа -->
      <div v-else-if="placeholderImg" class="product-media-slider__single">
        <img
          :src="placeholderImg"
          loading="lazy"
          class="product-media-slider__media"
          :class="{ 'product-media-slider__media--on-background': onBackground }"
          :alt="currentVariant.name"
        />
      </div>
    </ClientOnly>
  </div>
</template>

<style scoped lang="scss">
.product-media-slider {
  @apply relative min-w-[0] h-fit lg:h-full;

  @media (max-width: 768px) {
    max-height: 40vh;
  }

  /* Потолок для КОРОТКОГО ЭКРАНА — это про телефон в альбомной ориентации, где
     модалка занимает весь экран и блюду с текстом надо ужиться по вертикали.
     Без ограничения по ширине то же правило срабатывало и на ноутбуке с невысоким
     окном (1024×640): фото в своей колонке сжималось до 26vh, и блюдо превращалось
     в марку посреди пустой подложки. На десктопе высоту задаёт сама модалка. */
  @media (max-height: 740px) and (max-width: 1023px) {
    max-height: 26vh;
  }

  &__slider {
    @apply w-full h-full;
  }

  &__swiper {
    @apply w-full h-full max-sm:rounded-b-3xl overflow-hidden;

    // CSS-переменные для стилизации пагинации Swiper
    --swiper-pagination-color: #ef4444;
    --swiper-pagination-bullet-inactive-color: #9ca3af;
    --swiper-pagination-bullet-inactive-opacity: 1;
    --swiper-pagination-bullet-size: 10px;
    --swiper-pagination-bullet-horizontal-gap: 4px;
    --swiper-pagination-bottom: 8px;
  }

  &__slide {
    @apply w-full h-full;
  }

  &__single {
    @apply overflow-hidden max-sm:rounded-b-3xl flex justify-center items-center;
    width: 100%;
    aspect-ratio: 1 / 1;

    @media (max-width: 768px) {
      max-height: 40vh;
    }
    @media (max-height: 740px) and (max-width: 1023px) {
      max-height: 23vh;
    }

    /* Десктоп: высоту колонки задаёт модалка (она ограничена вьюпортом), а не
       пропорция кадра. С жёстким 1:1 фото на колонке 571–662px требовало столько же
       по высоте — на окне 640px модалка не помещалась, а с потолком 23vh, наоборот,
       блюдо сжималось в марку. */
    @media (min-width: 1024px) {
      aspect-ratio: auto;
      height: 100%;
      max-height: none;
    }
  }

  &__media {
    @apply w-full h-full object-cover;

    // На своей подложке блюдо вписываем целиком: у прозрачного PNG нельзя срезать края
    &--on-background {
      @apply object-contain;
      padding: 4%;
    }
  }
}
</style>
